import { RefObject, useContext, useEffect, useMemo, useState } from 'react';
import { uniqBy } from 'lodash';
import styled from '@emotion/styled';
import {
  AutocompleteProps,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  MenuItemProps,
  TextField,
  Tooltip
} from '@mui/material';

import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { UserProfile } from '@/redux/slices/slice_types';
import { applyFilter } from '@/redux/slices/dataTableSlice';
import { selectUser } from '@/redux/selectors/authSelectors';
import { selectUserFiltedByType } from '@/redux/selectors/dataTableSelectors';
import axios from '@/_utils/axios';
import { PolarisIcon, UserDisplay } from '@/exports/columnCommonComponents';
import Autocomplete from '@/components/_common/Form/Autocomplete';
import { ClearSearchButton, CollapseButton, CollapseWrapper, ExpandButton, SelectedFilterChip } from '@/components/_common/Filters/FilterMultiSelect';

export const USER_UNASSIGNED = { id: -1, username: 'Unassigned', first_name: 'Unassigned', last_name: '' } as UserProfile ;

export type UserSearchProps = {
  onEscape?: () => void;
  onSelect: AutocompleteProps<any, any, any, any>['onChange'];
  size?: AutocompleteProps<any, any, any, any>['size'];
  value?: null | UserProfile | string;
  placeholder?: string;
  fullWidth?: boolean;
  isTableFilter?: boolean;
  hideUnassigned?: boolean;
  innerRef?: RefObject<any>;
  isActive?: boolean;
  type?: 'assigned_users_filter' | 'managed_by_filter';
  isQuickFilters?: boolean;
  isAdvanceFilter?: boolean;
  isExternal?: boolean;
  advanceFilters?: Record<string, any>;
  userList?: UserProfile[];
  onAdvanceFiltersChange?: (newAdvanceFilters: { type: string; selections: any }) => void;
};

const UserSearch = ({
  onSelect,
  onEscape,
  size = 'small',
  value,
  type = 'assigned_users_filter',
  placeholder = 'Start typing to search',
  fullWidth = false,
  isTableFilter = false,
  hideUnassigned = false,
  innerRef,
  isActive,
  isQuickFilters = false,
  isAdvanceFilter = false,
  isExternal = false,
  userList,
  advanceFilters = {},
  onAdvanceFiltersChange
}: UserSearchProps) => {
  const isFiltersV2 = useContext(ConfigContext)?.filtersVersion === 2;
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectUser);
  const selectedUsers = (isAdvanceFilter ? advanceFilters?.[type] || [] : useAppSelector(selectUserFiltedByType(type))) as UserProfile[];
  const [searchInput, setSearchInput] = useState<string>('');
  const [users, setUsers] = useState(userList?.length ? userList : [currentUser]);
  const [selectedFiltersExpanded, setSelectedFiltersExpanded] = useState(false);

  const handleChipClick = (id: number) => {
    const selections = selectedUsers.filter((user) => user.id !== id);
    const newValue = { type, selections };
    if (isAdvanceFilter && onAdvanceFiltersChange) {
      onAdvanceFiltersChange(newValue);
    } else {
      dispatch(applyFilter(newValue));
    }
  };

  const handleClearSearchButtonClick = () => {
    setSearchInput('');
  };

  const handleExpand = () => {
    setSelectedFiltersExpanded(true);
  };

  const handleCollapse = () => {
    setSelectedFiltersExpanded(false);
  };

  const renderOption = (props: MenuItemProps, option: UserProfile) => {
    const newProps = {
      ...props,
      key: option.id,
      className: props.className
    };
    if (!option) return '';
    return (
      <UserOption role="option" {...newProps}>
        <UserDisplay ellipsisOverflow user={option} iconSize={15} />
        <small>{option.email}</small>
      </UserOption>
    );
  };

  // add unassigned option to the top of the list
  const options = useMemo(() => {
    const opts = [...users];
    if (value && typeof value !== 'string') opts.push(value);

    if (!hideUnassigned) {
      opts.unshift(USER_UNASSIGNED);
    }

    return uniqBy(opts, 'id');
  }, [users, hideUnassigned, value]);

  const chipsToDisplay = useMemo(() => {
    if (selectedFiltersExpanded) return { count: selectedUsers.length, characterLength: 0 };

    let count = 0;
    let characterLength = 0;

    if (isFiltersV2) {
      for (const curr of selectedUsers) {
        const nextCount = count + 1;
        const nextCharacterLength = characterLength + (curr.email?.length || 0);
        if (nextCharacterLength > 50 || nextCount > 2) {
          break;
        }

        count = nextCount;
        characterLength = nextCharacterLength;
      }
    }

    return { count, characterLength };
  }, [isFiltersV2, selectedUsers, selectedFiltersExpanded]);

  const filterUserOptions: NonNullable<AutocompleteProps<any, any, any, any>['filterOptions']> = (availableOptions, state) => {
    const query = state.inputValue.trim().toLowerCase();

    if (!query) return availableOptions;

    return availableOptions.filter((option: UserProfile) => {
      const searchableValues = [option.id, option.first_name, option.last_name, option.email]
        .filter((value) => value !== null && value !== undefined)
        .map((value) => String(value).toLowerCase());

      return searchableValues.some((value) => value.includes(query));
    });
  };

  useEffect(() => {
    if (searchInput && searchInput.length > 1) {
      if (userList?.length) {
        setUsers(userList);
      } else {
        axios.post(`/auth/users/search/`, { search: searchInput, is_active: isActive, external: isExternal }).then((res) => {
          const list = res.data?.data || [];
          const dataWithoutCurrentUser = list.filter((user: any) => user.email !== currentUser.email);
          setUsers([currentUser, ...dataWithoutCurrentUser]);
        });
      }
    }
    return () => {
      setUsers(userList?.length ? userList : [currentUser]);
    };
  }, [searchInput, isExternal, currentUser, userList]);

  useEffect(
    function collapseOnClear() {
      if (selectedFiltersExpanded && selectedUsers.length === 0) {
        handleCollapse();
      }
    },
    [selectedUsers, selectedFiltersExpanded]
  );

  return isTableFilter && isFiltersV2 && !isQuickFilters ? (
    <StyledBox>
      {selectedUsers.length > 0 && (
        <SelectedUsersWrapper>
          {selectedUsers.slice(0, chipsToDisplay.count).map((su) => (
            <SelectedFilterChip
              key={su.id}
              label={su.email || su.username}
              deleteIcon={<PolarisIcon icon="close" height={16} />}
              onClick={() => handleChipClick(su.id as number)}
              onDelete={() => handleChipClick(su.id as number)}
            />
          ))}
          {selectedUsers.length > chipsToDisplay.count && !selectedFiltersExpanded && (
            <Tooltip title="Show more">
              <ExpandButton variant="text" onClick={handleExpand}>
                ...
              </ExpandButton>
            </Tooltip>
          )}
          {selectedUsers.length > 0 && <Badge>{selectedUsers.length}</Badge>}
          {selectedFiltersExpanded && (
            <CollapseWrapper>
              <CollapseButton
                variant="text"
                size="small"
                color="default"
                startIcon={<PolarisIcon icon="subtract" height={16} width={8} />}
                onClick={handleCollapse}
              >
                Show less
              </CollapseButton>
            </CollapseWrapper>
          )}
        </SelectedUsersWrapper>
      )}
      <SearchField
        fullWidth
        value={searchInput}
        label="Quick Search Assignee"
        inputRef={innerRef}
        isFiltersV2={isFiltersV2}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {searchInput.length > 0 ? (
                <ClearSearchButton onClick={handleClearSearchButtonClick}>
                  <PolarisIcon icon="close_filled" />
                </ClearSearchButton>
              ) : (
                <PolarisIcon icon="search" />
              )}
            </InputAdornment>
          )
        }}
        onChange={(e) => setSearchInput(e.target.value)}
      />
      <StyledList>
        {options?.length > (searchInput && searchInput.length > 1 ? 2 : 0) ? (
          options.slice(searchInput && searchInput.length > 1 ? 2 : 0).map((option, index) => {
            return (
              <ListItem key={option.id} tabIndex={index + 1} disablePadding>
                <StyledListItemButton dense onClick={(e) => onSelect!(e, option, 'selectOption')}>
                  <ListItemText primary={<UserDisplay ellipsisOverflow user={option} iconSize={16} />} secondary={option.email} />
                </StyledListItemButton>
              </ListItem>
            );
          })
        ) : (
          <ListItem disablePadding>
            <ListItemText primary="No users found." />
          </ListItem>
        )}
      </StyledList>
    </StyledBox>
  ) : (
    <Autocomplete
      id="assignee-filter"
      label={isTableFilter ? 'Quick Search Assignee' : ''}
      size={(isTableFilter ? 'large' : size) as AutocompleteProps<any, any, any, any>['size']}
      options={options}
      value={value || null}
      onInputChange={(_e, val: string) => setSearchInput(val)}
      sx={{ width: fullWidth ? '100%' : 200 }}
      getOptionLabel={(option: UserProfile) => (`${option.first_name} ${option.last_name}`).trim()}
      renderOption={renderOption}
      noOptionsText="No users found."
      openOnFocus={false}
      onChange={onSelect as AutocompleteProps<any, any, any, any>['onChange']}
      onEscape={onEscape}
      placeholder={placeholder}
      blurOnSelect
      inputProps={{ hiddenLabel: true }}
      isOptionEqualToValue={(option: UserProfile, value: UserProfile) => option?.id === value?.id}
      variant={isTableFilter ? 'outlined' : 'filled'}
      innerRef={innerRef as any}
      filterOptions={filterUserOptions}
    />
  );
};

export default UserSearch;

const UserOption = styled(MenuItem)(({ theme }) => ({
  width: '100%',
  overflow: 'hidden',
  flexDirection: 'column',

  '&.MuiMenuItem-root': {
    padding: theme.spacing(1.5),
    display: 'flex',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    alignItems: 'flex-start',
    lineHeight: 1.2
  },

  '& svg': {
    minWidth: 15
  },

  '& small': {
    opacity: 0.5,
    fontSize: '0.85rem',
    width: '100%',
    display: 'block',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
  }
}));

const StyledBox = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2),
  gap: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius
}));

const SelectedUsersWrapper = styled.div(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  alignItems: 'center'
}));

const SearchField = styled(TextField, { shouldForwardProp: (prop) => prop !== 'isFiltersV2' })<{ isFiltersV2: boolean }>(
  ({ theme, isFiltersV2 }) => ({
    '& .MuiOutlinedInput-root': {
      backgroundColor: isFiltersV2
        ? theme.palette?.filters?.input?.background || (theme.palette.mode === 'dark' ? '#25272C' : '#FFFFFF')
        : theme.customOverrides.Calendar?.background || '#303240',
      '&.Mui-focused': {
        backgroundColor: isFiltersV2
          ? theme.palette?.filters?.input?.focused || (theme.palette.mode === 'dark' ? '#313339' : '#FFFFFF')
          : theme.customOverrides.Calendar?.background || '#303240'
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: isFiltersV2
          ? theme.palette?.filters?.input?.border || (theme.palette.mode === 'dark' ? '#313339' : '#a4a4a4')
          : 'rgba(255, 255, 255, 0.23)'
      }
    },
    '& .MuiInputAdornment-root': {
      color: theme.palette.primary.main
    }
  })
);

const StyledList = styled(List)(({ theme }) => ({
  overflow: 'auto',
  maxHeight: 200,
  padding: theme.spacing(1, 0, 0)
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  padding: theme.spacing(0.5, 0, 0.5, 1)
}));

const Badge = styled.span(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  fontSize: '0.75rem',
  paddingLeft: 'auto',
  borderRadius: 12,
  height: 'fit-content',
  width: 'fit-content',
  padding: theme.spacing(0.25, 1.5),
  lineHeight: 2,
  marginLeft: 'auto'
}));
